/*
 * Lightweight utility for interacting with Elevar's global data layer.
 * This file stays intentionally dependency-free so it can be tree-shaken in
 * consuming apps that do not import it.
 */
'use client';
import type { ElevarEventPayload } from './elevar-types';

// Window typing helper
declare global {
  interface Window {
    ElevarDataLayer?: ElevarEventPayload[] & {
      push: (event: ElevarEventPayload) => number;
    };
    ElevarInvalidateContext?: () => void;
    ElevarDebugMode?: (enable: boolean) => void;
  }
}

function ensureDataLayer() {
  if (typeof window === 'undefined') return;
  window.ElevarDataLayer =
    window.ElevarDataLayer ?? ([] as unknown as typeof window.ElevarDataLayer);
}

export function pushElevarEvent(event: ElevarEventPayload) {
  if (typeof window === 'undefined') return;

  ensureDataLayer();

  // Some apps render server-side and hydrate on the client. To avoid hydration
  // mismatches we guard the call behind requestAnimationFrame so it always
  // happens on the client after hydration.
  requestAnimationFrame(() => {
    window.ElevarDataLayer?.push(event);
  });
}

/**
 * When navigating client side we need to inform Elevar of the route change so
 * that UTMs/cookies can be refreshed. Apps can call this util after a route
 * transition.
 */
export function invalidateElevarContext() {
  if (typeof window !== 'undefined' && window.ElevarInvalidateContext) {
    window.ElevarInvalidateContext();
  }
}
